
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import StepCard from '../../../../components/wizard/StepCard';
import CityAutocomplete from '../../../../components/base/CityAutocomplete';
import { WizardData } from '../../../../types/wizard';
import { destinations } from '../../../../mocks/cities';

function normalizeTripValue(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

interface Step6PreviousTripsProps {
  data: WizardData;
  onChange: (field: keyof WizardData, value: any) => void;
  onAdvance: () => void;
  error?: string;
}

export default function Step6PreviousTrips({ data, onChange, onAdvance, error }: Step6PreviousTripsProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.resolvedLanguage?.startsWith('en');
  const [inputValue, setInputValue] = useState('');
  const selectedCities = data.previousTrips || [];

  const handleCityChange = (city: string) => {
    setInputValue(city);
  };

  const handleCommitCity = (city: string) => {
    const trimmedCity = city.trim();
    if (!trimmedCity) return;

    const normalizedCity = normalizeTripValue(trimmedCity);
    const alreadySelected = selectedCities.some(
      (selectedCity) => normalizeTripValue(selectedCity) === normalizedCity,
    );

    if (alreadySelected) {
      setInputValue('');
      return;
    }

    onChange('previousTrips', [...selectedCities, trimmedCity]);
    setInputValue('');
  };

  const handleRemoveCity = (city: string) => {
    const updated = selectedCities.filter((c) => c !== city);
    onChange('previousTrips', updated);
  };

  const handleSkip = () => {
    onChange('previousTrips', []);
    onAdvance();
  };

  return (
    <StepCard>
      <div className="mb-6">
        <h3 className="text-white text-lg font-medium mb-2">{isEnglish ? 'Previous Trips' : 'Viagens Anteriores'}</h3>
        <p className="text-white/90 text-sm">
          {isEnglish
            ? 'Tell us about the destinations you have already visited. This helps us suggest new experiences and avoid repetition.'
            : 'Conte-nos sobre os destinos que você já visitou. Isso nos ajuda a sugerir experiências novas e evitar repetições.'}
        </p>
      </div>

      <div className="space-y-6">
        {/* City Autocomplete */}
        <div>
          <label className="block text-sm font-medium text-white/90 mb-3">
            {isEnglish ? 'Which destinations have you already visited?' : 'Quais destinos você já visitou?'}
          </label>
          <CityAutocomplete
            id="previousTripsSearch"
            name="previousTripsSearch"
            value={inputValue}
            onChange={handleCityChange}
            onCommit={handleCommitCity}
            suggestions={destinations}
            placeholder={isEnglish ? 'Type the name of a destination...' : 'Digite o nome de um destino...'}
            icon="ri-search-line"
            allowCustomValue
            customValueLabel={(value) =>
              isEnglish ? `Add "${value}"` : `Adicionar "${value}"`
            }
          />
          <p className="text-xs text-white/90 mt-2">
            {isEnglish
              ? 'Type and select destinations from the list, or add your own if it is not listed.'
              : 'Digite e selecione destinos da lista, ou adicione manualmente se ele não aparecer.'}
          </p>
        </div>

        {/* Selected Cities */}
        {selectedCities.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-white/90">
                {isEnglish ? `Visited destinations (${selectedCities.length})` : `Destinos visitados (${selectedCities.length})`}
              </span>
              <button
                type="button"
                onClick={handleSkip}
                className="text-xs text-white/90 hover:text-white transition-colors cursor-pointer"
              >
                {isEnglish ? 'Clear all' : 'Limpar todos'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedCities.map((city) => (
                <div
                  key={city}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-white/90 hover:bg-white/10 transition-colors"
                >
                  <span>{city}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveCity(city)}
                    className="w-4 h-4 flex items-center justify-center text-white/90 hover:text-red-400 transition-colors cursor-pointer"
                    aria-label={isEnglish ? `Remove ${city}` : `Remover ${city}`}
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {selectedCities.length === 0 && (
          <div className="text-center py-8">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-white/5 rounded-full">
              <i className="ri-map-pin-line text-2xl text-white/90"></i>
            </div>
            <p className="text-sm text-white/90 mb-4">{isEnglish ? 'No destination added yet' : 'Nenhum destino adicionado ainda'}</p>
            <button
              type="button"
              onClick={handleSkip}
              className="text-sm text-[#FFD700] hover:text-[#FFD700]/80 transition-colors cursor-pointer whitespace-nowrap"
            >
              {isEnglish ? 'Skip this step →' : 'Pular esta etapa →'}
            </button>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <i className="ri-error-warning-line text-red-400"></i>
            <span className="text-sm text-red-400">{error}</span>
          </div>
        )}

        <div className="p-4 bg-[#FFD700]/5 border border-[#FFD700]/20 rounded-lg">
          <div className="flex gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-[#FFD700]"></i>
            </div>
            <p className="text-xs text-white/90 leading-relaxed">
              <strong className="text-white/90">{isEnglish ? 'Tip:' : 'Dica:'}</strong>{' '}
              {isEnglish
                ? 'Sharing previous experiences helps us build a more personalized itinerary, avoiding repeated destinations and suggesting complementary places.'
                : 'Compartilhar suas experiências anteriores nos ajuda a criar um roteiro mais personalizado, evitando destinos repetidos e sugerindo lugares complementares.'}
            </p>
          </div>
        </div>
      </div>
    </StepCard>
  );
}
